import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { of, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SecurityService } from '../services/security.service';
import { SecurityRole } from 'src/app/utils/enums/enums/general/role.enum';
import { ContractRole } from 'src/app/utils/enums/enums/contract-role/contract-role.enum';
import { ContractService } from 'src/app/contract/contract.service';

export const contractEditorGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {
  const router = inject(Router);
  const securityService = inject(SecurityService);
  const contractService = inject(ContractService);

  const uuid = route.paramMap.get('uuid') ?? '';
  const loggedUser = securityService.getLoggedUser();

  if (!securityService.isLoggedIn() || !loggedUser) {
    return router.createUrlTree(['/login']);
  }

  if (!uuid) {
    return router.createUrlTree(['/dashboard']);
  }

  // Admin: total access, no HTTP call needed
  if (securityService.isSecurityRoleUser(SecurityRole.GOVERNYS_ADMINISTRATOR)) {
    return true;
  }

  const isExternalRoute = state.url.startsWith('/external/contract/editor/');
  const isExternalUser = securityService.isSecurityRoleUser(SecurityRole.EXTERNAL);
  const dashboard = router.createUrlTree(['/dashboard']);

  return contractService.isUserInContract(uuid).pipe(
    map((role: ContractRole | null) => {
      // Not assigned to the contract
      if (!role) {
        return dashboard;
      }

      const hasExternalRoleOnContract = role.startsWith('EXTERNAL_');

      // EXTERNAL user: must have an external role AND be on the external route
      if (isExternalUser) {
        if (!hasExternalRoleOnContract) {
          return dashboard;
        }
        return isExternalRoute
          ? true
          : router.createUrlTree(['/external/contract/editor', uuid]);
      }

      // Internal user landing on the external route without an external role
      if (isExternalRoute && !hasExternalRoleOnContract) {
        return router.createUrlTree(['/contract-editor', uuid]);
      }

      return true;
    }),
    catchError(() => of(dashboard))
  );
};
